/*
 * If not stated otherwise in this file or this component's LICENSE file the
 * following copyright and licenses apply:
 *
 * Copyright 2023 Comcast Cable Communications Management, LLC.
 *
 * Licensed under the Apache License, Version 2.0 (the License);
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { assertTruthy, getNewId, mergeColorAlphaPremultiplied, } from '../utils.js';
import { EventEmitter } from '../common/EventEmitter.js';
import { copyRect, intersectRect, createBound, boundInsideBound, } from './lib/utils.js';
import { Matrix3d } from './lib/Matrix3d.js';
import { RenderCoords } from './lib/RenderCoords.js';
import { CoreAnimation } from './animations/CoreAnimation.js';
import { CoreAnimationController } from './animations/CoreAnimationController.js';
export var CoreNodeRenderState;
(function (CoreNodeRenderState) {
    CoreNodeRenderState[CoreNodeRenderState["Init"] = 0] = "Init";
    CoreNodeRenderState[CoreNodeRenderState["OutOfBounds"] = 2] = "OutOfBounds";
    CoreNodeRenderState[CoreNodeRenderState["InBounds"] = 4] = "InBounds";
    CoreNodeRenderState[CoreNodeRenderState["InViewport"] = 8] = "InViewport";
})(CoreNodeRenderState || (CoreNodeRenderState = {}));
const CoreNodeRenderStateMap = new Map();
CoreNodeRenderStateMap.set(CoreNodeRenderState.Init, 'init');
CoreNodeRenderStateMap.set(CoreNodeRenderState.OutOfBounds, 'outOfBounds');
CoreNodeRenderStateMap.set(CoreNodeRenderState.InBounds, 'inBounds');
CoreNodeRenderStateMap.set(CoreNodeRenderState.InViewport, 'inViewport');
export var UpdateType;
(function (UpdateType) {
    /**
     * Child updates
     */
    UpdateType[UpdateType["Children"] = 1] = "Children";
    /**
     * Scale/Rotate transform update
     *
     * @remarks
     * CoreNode Properties Updated:
     * - `scaleRotateTransform`
     */
    UpdateType[UpdateType["ScaleRotate"] = 2] = "ScaleRotate";
    /**
     * Translate transform update (x/y/width/height/pivot/mount)
     *
     * @remarks
     * CoreNode Properties Updated:
     * - `localTransform`
     */
    UpdateType[UpdateType["Local"] = 4] = "Local";
    /**
     * Global Transform update
     *
     * @remarks
     * CoreNode Properties Updated:
     * - `globalTransform`
     * - `renderCoords`
     * - `renderBound`
     */
    UpdateType[UpdateType["Global"] = 8] = "Global";
    /**
     * Clipping rect update
     *
     * @remarks
     * CoreNode Properties Updated:
     * - `clippingRect`
     */
    UpdateType[UpdateType["Clipping"] = 16] = "Clipping";
    /**
     * Calculated ZIndex update
     *
     * @remarks
     * CoreNode Properties Updated:
     * - `calcZIndex`
     */
    UpdateType[UpdateType["CalculatedZIndex"] = 32] = "CalculatedZIndex";
    /**
     * Z-Index Sorted Children update
     *
     * @remarks
     * CoreNode Properties Updated:
     * - `children` (sorts children by their `calcZIndex`)
     */
    UpdateType[UpdateType["ZIndexSortedChildren"] = 64] = "ZIndexSortedChildren";
    /**
     * Premultiplied Colors update
     *
     * @remarks
     * CoreNode Properties Updated:
     * - `premultipliedColorTl`
     * - `premultipliedColorTr`
     * - `premultipliedColorBl`
     * - `premultipliedColorBr`
     */
    UpdateType[UpdateType["PremultipliedColors"] = 128] = "PremultipliedColors";
    /**
     * World Alpha update
     *
     * @remarks
     * CoreNode Properties Updated:
     * - `worldAlpha` = `parent.worldAlpha` * `alpha`
     */
    UpdateType[UpdateType["WorldAlpha"] = 256] = "WorldAlpha";
    /**
     * Render State update
     *
     * @remarks
     * CoreNode Properties Updated:
     * - `renderState`
     */
    UpdateType[UpdateType["RenderState"] = 512] = "RenderState";
    /**
     * Is Renderable update
     *
     * @remarks
     * CoreNode Properties Updated:
     * - `isRenderable`
     */
    UpdateType[UpdateType["IsRenderable"] = 1024] = "IsRenderable";
    /**
     * Render Texture update
     */
    UpdateType[UpdateType["RenderTexture"] = 2048] = "RenderTexture";
    /**
     * Track if parent has render texture
     */
    UpdateType[UpdateType["ParentRenderTexture"] = 4096] = "ParentRenderTexture";
    /**
     * None
     */
    UpdateType[UpdateType["None"] = 0] = "None";
    /**
     * All
     */
    UpdateType[UpdateType["All"] = 8191] = "All";
})(UpdateType || (UpdateType = {}));
/**
 * A visual Node in the Renderer scene graph.
 *
 * @remarks
 * CoreNode is an internally used class that represents a Renderer Node in the
 * scene graph. See INode.ts for the public APIs exposed to Renderer users
 * that include generic types for Shaders.
 */
export class CoreNode extends EventEmitter {
    stage;
    children = [];
    _id = getNewId();
    props;
    updateType = UpdateType.All;
    globalTransform;
    scaleRotateTransform;
    localTransform;
    renderCoords;
    renderBound;
    strictBound;
    preloadBound;
    clippingRect = {
        x: 0,
        y: 0,
        width: 0,
        height: 0,
        valid: false,
    };
    isRenderable = false;
    renderState = CoreNodeRenderState.Init;
    worldAlpha = 1;
    premultipliedColorTl = 0;
    premultipliedColorTr = 0;
    premultipliedColorBl = 0;
    premultipliedColorBr = 0;
    calcZIndex = 0;
    hasRTTupdates = false;
    parentHasRenderTexture = false;
    constructor(stage, props) {
        super();
        this.stage = stage;
        this.props = {
            ...props,
            parent: null,
            texture: null,
            src: null,
            rtt: false,
        };
        // Assign props to instance
        this.parent = props.parent;
        this.texture = props.texture;
        this.src = props.src;
        this.rtt = props.rtt;
        this.updateScaleRotateTransform();
    }
    //#region Textures
    loadTexture() {
        const { texture } = this.props;
        assertTruthy(texture);
        // If texture is already loaded / failed, trigger loaded event manually
        // so that users get a consistent event experience.
        // We do this in a microtask to allow listeners to be attached in the same
        // synchronous task after calling loadTexture()
        queueMicrotask(() => {
            // Preload texture if required
            if (this.textureOptions.preload) {
                texture.ctxTexture.load();
            }
            if (texture.state === 'loaded') {
                assertTruthy(texture.dimensions);
                this.onTextureLoaded(texture, texture.dimensions);
            }
            else if (texture.state === 'failed') {
                assertTruthy(texture.error);
                this.onTextureFailed(texture, texture.error);
            }
            else if (texture.state === 'freed') {
                this.onTextureFreed(texture);
            }
            texture.on('loaded', this.onTextureLoaded);
            texture.on('failed', this.onTextureFailed);
            texture.on('freed', this.onTextureFreed);
        });
    }
    unloadTexture() {
        if (this.texture) {
            this.texture.off('loaded', this.onTextureLoaded);
            this.texture.off('failed', this.onTextureFailed);
            this.texture.off('freed', this.onTextureFreed);
            this.texture.setRenderableOwner(this, false);
        }
    }
    autosizeNode(dimensions) {
        if (this.autosize) {
            this.width = dimensions.width;
            this.height = dimensions.height;
        }
    }
    onTextureLoaded = (_, dimensions) => {
        this.autosizeNode(dimensions);
        // Texture was loaded. In case the RAF loop has already stopped, we request
        // a render to ensure the texture is rendered.
        this.stage.requestRender();
        // If parent has a render texture, flag that we need to update
        // @todo: Reserve type for RTT updates
        if (this.parentHasRenderTexture) {
            this.setRTTUpdates(1);
        }
        this.emit('loaded', {
            type: 'texture',
            dimensions,
        });
        // Trigger a local update if the texture is loaded and the resizeMode is 'contain'
        if (this.props.textureOptions?.resizeMode?.type === 'contain') {
            this.setUpdateType(UpdateType.Local);
        }
    };
    onTextureFailed = (_, error) => {
        this.emit('failed', {
            type: 'texture',
            error,
        });
    };
    onTextureFreed = () => {
        this.emit('freed', {
            type: 'texture',
        });
    };
    //#endregion Textures
    /**
     * Change types types is used to determine the scope of the changes being applied
     *
     * @remarks
     * See {@link UpdateType} for more information on each type
     *
     * @param type
     */
    setUpdateType(type) {
        this.updateType |= type;
        // If we're updating this node at all, we need to inform the parent
        // (and all ancestors) that their children need updating as well
        const parent = this.props.parent;
        if (parent && !(parent.updateType & UpdateType.Children)) {
            parent.setUpdateType(UpdateType.Children);
        }
        // If node is part of RTT texture
        // Flag that we need to update
        if (this.parentHasRenderTexture) {
            this.setRTTUpdates(type);
        }
    }
    sortChildren() {
        this.children.sort((a, b) => a.calcZIndex - b.calcZIndex);
    }
    updateScaleRotateTransform() {
        const { rotation, scaleX, scaleY } = this.props;
        // optimize simple translation cases
        if (rotation === 0 && scaleX === 1 && scaleY === 1) {
            this.scaleRotateTransform = undefined;
            return;
        }
        this.scaleRotateTransform = Matrix3d.rotate(rotation, this.scaleRotateTransform).scale(scaleX, scaleY);
    }
    updateLocalTransform() {
        const { x, y, width, height } = this.props;
        const mountTranslateX = this.props.mountX * width;
        const mountTranslateY = this.props.mountY * height;
        if (this.scaleRotateTransform) {
            const pivotTranslateX = this.props.pivotX * width;
            const pivotTranslateY = this.props.pivotY * height;
            this.localTransform = Matrix3d.translate(x - mountTranslateX + pivotTranslateX, y - mountTranslateY + pivotTranslateY, this.localTransform)
                .multiply(this.scaleRotateTransform)
                .translate(-pivotTranslateX, -pivotTranslateY);
        }
        else {
            this.localTransform = Matrix3d.translate(x - mountTranslateX, y - mountTranslateY, this.localTransform);
        }
        // Handle 'contain' resize mode
        const texture = this.props.texture;
        if (texture &&
            texture.dimensions &&
            this.props.textureOptions?.resizeMode?.type === 'contain') {
            let resizeModeScaleX = 1;
            let resizeModeScaleY = 1;
            let extraX = 0;
            let extraY = 0;
            const { width: tw, height: th } = texture.dimensions;
            const txAspectRatio = tw / th;
            const nodeAspectRatio = width / height;
            if (txAspectRatio > nodeAspectRatio) {
                // Texture is wider than node
                // Center the node vertically (shift down by extraY)
                // Scale the node vertically to maintain original aspect ratio
                const scaleX = width / tw;
                const scaledTxHeight = th * scaleX;
                extraY = (height - scaledTxHeight) / 2;
                resizeModeScaleY = scaledTxHeight / height;
            }
            else {
                // Texture is taller than node (or equal)
                // Center the node horizontally (shift right by extraX)
                // Scale the node horizontally to maintain original aspect ratio
                const scaleY = height / th;
                const scaledTxWidth = tw * scaleY;
                extraX = (width - scaledTxWidth) / 2;
                resizeModeScaleX = scaledTxWidth / width;
            }
            // Apply the extra translation and scale to the local transform
            this.localTransform
                .translate(extraX, extraY)
                .scale(resizeModeScaleX, resizeModeScaleY);
        }
        this.setUpdateType(UpdateType.Global);
    }
    /**
     * @todo: test for correct calculation flag
     * @param delta
     */
    update(delta, parentClippingRect) {
        if (this.updateType & UpdateType.ScaleRotate) {
            this.updateScaleRotateTransform();
            this.setUpdateType(UpdateType.Local);
        }
        if (this.updateType & UpdateType.Local) {
            this.updateLocalTransform();
            this.setUpdateType(UpdateType.Global);
        }
        const parent = this.props.parent;
        let childUpdateType = UpdateType.None;
        if (this.updateType & UpdateType.ParentRenderTexture) {
            let p = this.parent;
            while (p) {
                if (p.rtt) {
                    this.parentHasRenderTexture = true;
                }
                p = p.parent;
            }
        }
        // If we have render texture updates and not already running a full update
        if (this.updateType ^ UpdateType.All &&
            this.updateType & UpdateType.RenderTexture) {
            this.children.forEach((child) => {
                child.setUpdateType(UpdateType.All);
            });
        }
        if (this.updateType & UpdateType.Global) {
            assertTruthy(this.localTransform);
            this.globalTransform = Matrix3d.copy(parent?.globalTransform || this.localTransform, this.globalTransform);
            if (this.parentHasRenderTexture && this.props.parent?.rtt) {
                this.globalTransform = Matrix3d.identity();
            }
            if (parent) {
                this.globalTransform.multiply(this.localTransform);
            }
            this.calculateRenderCoords();
            this.updateBoundingRect();
            this.setUpdateType(UpdateType.Clipping | UpdateType.RenderState | UpdateType.Children);
            childUpdateType |= UpdateType.Global;
        }
        if (this.updateType & UpdateType.Clipping) {
            this.calculateClippingRect(parentClippingRect);
            this.setUpdateType(UpdateType.Children);
            childUpdateType |= UpdateType.Clipping;
        }
        if (this.updateType & UpdateType.WorldAlpha) {
            if (parent) {
                this.worldAlpha = parent.worldAlpha * this.props.alpha;
            }
            else {
                this.worldAlpha = this.props.alpha;
            }
            this.setUpdateType(UpdateType.Children |
                UpdateType.PremultipliedColors |
                UpdateType.IsRenderable);
            childUpdateType |= UpdateType.WorldAlpha;
        }
        if (this.updateType & UpdateType.PremultipliedColors) {
            this.premultipliedColorTl = mergeColorAlphaPremultiplied(this.props.colorTl, this.worldAlpha, true);
            // If all the colors are the same just sent them all to the same value
            if (this.props.colorTl === this.props.colorTr &&
                this.props.colorBl === this.props.colorBr &&
                this.props.colorTl === this.props.colorBl) {
                this.premultipliedColorTr =
                    this.premultipliedColorBl =
                        this.premultipliedColorBr =
                            this.premultipliedColorTl;
            }
            else {
                this.premultipliedColorTr = mergeColorAlphaPremultiplied(this.props.colorTr, this.worldAlpha, true);
                this.premultipliedColorBl = mergeColorAlphaPremultiplied(this.props.colorBl, this.worldAlpha, true);
                this.premultipliedColorBr = mergeColorAlphaPremultiplied(this.props.colorBr, this.worldAlpha, true);
            }
        }
        if (this.updateType & UpdateType.RenderState) {
            this.updateRenderState(parentClippingRect);
            this.setUpdateType(UpdateType.IsRenderable);
        }
        if (this.updateType & UpdateType.IsRenderable) {
            this.updateIsRenderable();
        }
        // No need to update zIndex if there is no parent
        if (parent && this.updateType & UpdateType.CalculatedZIndex) {
            this.calculateZIndex();
            // Tell parent to re-sort children
            parent.setUpdateType(UpdateType.ZIndexSortedChildren);
        }
        if (this.updateType & UpdateType.Children &&
            this.children.length &&
            !this.rtt) {
            this.children.forEach((child) => {
                // Trigger the depenedent update types on the child
                child.setUpdateType(childUpdateType);
                // If child has no updates, skip
                if (child.updateType === 0) {
                    return;
                }
                child.update(delta, this.clippingRect);
            });
        }
        // Sorting children MUST happen after children have been updated so
        // that they have the oppotunity to update their calculated zIndex.
        if (this.updateType & UpdateType.ZIndexSortedChildren) {
            // reorder z-index
            this.sortChildren();
        }
        // reset update type
        this.updateType = 0;
    }
    //check if CoreNode is renderable based on props
    checkRenderProps() {
        if (this.props.texture) {
            return true;
        }
        if (!this.props.width || !this.props.height) {
            return false;
        }
        if (this.props.shader !== this.stage.defShaderCtr) {
            return true;
        }
        if (this.props.clipping) {
            return true;
        }
        if (this.props.color !== 0) {
            return true;
        }
        // Consider removing these checks and just using the color property check above.
        // Maybe add a forceRender prop for nodes that should always render.
        if (this.props.colorTop !== 0) {
            return true;
        }
        if (this.props.colorBottom !== 0) {
            return true;
        }
        if (this.props.colorLeft !== 0) {
            return true;
        }
        if (this.props.colorRight !== 0) {
            return true;
        }
        if (this.props.colorTl !== 0) {
            return true;
        }
        if (this.props.colorTr !== 0) {
            return true;
        }
        if (this.props.colorBl !== 0) {
            return true;
        }
        if (this.props.colorBr !== 0) {
            return true;
        }
        return false;
    }
    checkRenderBounds(parentClippingRect) {
        assertTruthy(this.renderBound);
        const rectW = parentClippingRect.width || this.stage.root.width;
        const rectH = parentClippingRect.height || this.stage.root.height;
        this.strictBound = createBound(parentClippingRect.x, parentClippingRect.y, parentClippingRect.x + rectW, parentClippingRect.y + rectH, this.strictBound);
        if (boundInsideBound(this.renderBound, this.strictBound)) {
            return CoreNodeRenderState.InViewport;
        }
        const renderM = this.stage.boundsMargin;
        this.preloadBound = createBound(this.strictBound.x1 - renderM[3], this.strictBound.y1 - renderM[0], this.strictBound.x2 + renderM[1], this.strictBound.y2 + renderM[2], this.preloadBound);
        if (boundInsideBound(this.renderBound, this.preloadBound)) {
            return CoreNodeRenderState.InBounds;
        }
        return CoreNodeRenderState.OutOfBounds;
    }
    updateRenderState(parentClippingRect) {
        const renderState = this.checkRenderBounds(parentClippingRect);
        if (renderState === this.renderState) {
            return;
        }
        const previous = this.renderState;
        this.renderState = renderState;
        const event = CoreNodeRenderStateMap.get(renderState);
        assertTruthy(event);
        this.emit(event, {
            previous,
            current: renderState,
        });
    }
    /**
     * This function updates the `isRenderable` property based on certain conditions.
     *
     * @returns
     */
    updateIsRenderable() {
        let newIsRenderable;
        if (this.worldAlpha === 0 || !this.checkRenderProps()) {
            newIsRenderable = false;
        }
        else {
            newIsRenderable = this.renderState > CoreNodeRenderState.OutOfBounds;
        }
        if (this.isRenderable !== newIsRenderable) {
            this.isRenderable = newIsRenderable;
            this.onChangeIsRenderable(newIsRenderable);
        }
    }
    onChangeIsRenderable(isRenderable) {
        this.texture?.setRenderableOwner(this, isRenderable);
    }
    calculateRenderCoords() {
        const { width, height, globalTransform: transform } = this;
        assertTruthy(transform);
        const { tx, ty, ta, tb, tc, td } = transform;
        if (tb === 0 && tc === 0) {
            const minX = tx;
            const maxX = tx + width * ta;
            const minY = ty;
            const maxY = ty + height * td;
            this.renderCoords = RenderCoords.translate(
            //top-left
            minX, minY, 
            //top-right
            maxX, minY, 
            //bottom-right
            maxX, maxY, 
            //bottom-left
            minX, maxY, this.renderCoords);
        }
        else {
            this.renderCoords = RenderCoords.translate(
            //top-left
            tx, ty, 
            //top-right
            tx + width * ta, ty + width * tc, 
            //bottom-right
            tx + width * ta + height * tb, ty + width * tc + height * td, 
            //bottom-left
            tx + height * tb, ty + height * td, this.renderCoords);
        }
    }
    updateBoundingRect() {
        const { renderCoords, globalTransform: transform } = this;
        assertTruthy(transform);
        assertTruthy(renderCoords);
        const { tb, tc } = transform;
        const { x1, y1, x3, y3 } = renderCoords;
        if (tb === 0 || tc === 0) {
            this.renderBound = createBound(x1, y1, x3, y3, this.renderBound);
        }
        else {
            const { x2, x4, y2, y4 } = renderCoords;
            this.renderBound = createBound(Math.min(x1, x2, x3, x4), Math.min(y1, y2, y3, y4), Math.max(x1, x2, x3, x4), Math.max(y1, y2, y3, y4), this.renderBound);
        }
    }
    /**
     * This function calculates the clipping rectangle for a node.
     *
     * The function then checks if the node is rotated. If the node requires clipping and is not rotated, a new clipping rectangle is created based on the node's global transform and dimensions.
     * If a parent clipping rectangle exists, it is intersected with the node's clipping rectangle (if it exists), or replaces the node's clipping rectangle.
     *
     * Finally, the node's parentClippingRect and clippingRect properties are updated.
     */
    calculateClippingRect(parentClippingRect) {
        assertTruthy(this.globalTransform);
        const { clippingRect, props, globalTransform: gt } = this;
        const { clipping } = props;
        const isRotated = gt.tb !== 0 || gt.tc !== 0;
        if (clipping && !isRotated) {
            clippingRect.x = gt.tx;
            clippingRect.y = gt.ty;
            clippingRect.width = this.width * gt.ta;
            clippingRect.height = this.height * gt.td;
            clippingRect.valid = true;
        }
        else {
            clippingRect.valid = false;
        }
        if (parentClippingRect.valid && clippingRect.valid) {
            // Intersect parent clipping rect with node clipping rect
            intersectRect(parentClippingRect, clippingRect, clippingRect);
        }
        else if (parentClippingRect.valid) {
            // Copy parent clipping rect
            copyRect(parentClippingRect, clippingRect);
            clippingRect.valid = true;
        }
    }
    calculateZIndex() {
        const props = this.props;
        const z = props.zIndex || 0;
        const p = props.parent?.zIndex || 0;
        let zIndex = z;
        if (props.parent?.zIndexLocked) {
            zIndex = z < p ? z : p;
        }
        this.calcZIndex = zIndex;
    }
    /**
     * Destroy the node and cleanup all resources
     */
    destroy() {
        this.unloadTexture();
        this.clippingRect.valid = false;
        this.isRenderable = false;
        delete this.renderCoords;
        delete this.renderBound;
        delete this.strictBound;
        delete this.preloadBound;
        delete this.globalTransform;
        delete this.scaleRotateTransform;
        delete this.localTransform;
        this.props.texture = null;
        this.props.shader = this.stage.defShaderCtr;
        const children = [...this.children];
        for (let i = 0; i < children.length; i++) {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            children[i].destroy();
        }
        // This very action will also remove the node from the parent's children array
        this.parent = null;
        if (this.rtt) {
            this.stage.renderer.removeRTTNode(this);
        }
        this.removeAllListeners();
    }
    renderQuads(renderer) {
        const { texture, width, height, textureOptions, rtt, shader } = this.props;
        // Prevent quad rendering if parent has a render texture
        // and renderer is not currently rendering to a texture
        if (this.parentHasRenderTexture) {
            if (!renderer.renderToTextureActive) {
                return;
            }
            // Prevent quad rendering if parent render texture is not the active render texture
            if (this.parentRenderTexture !== renderer.activeRttNode) {
                return;
            }
        }
        const { premultipliedColorTl, premultipliedColorTr, premultipliedColorBl, premultipliedColorBr, } = this;
        const { zIndex, worldAlpha, globalTransform: gt, clippingRect, renderCoords, } = this;
        assertTruthy(gt);
        assertTruthy(renderCoords);
        // add to list of renderables to be sorted before rendering
        renderer.addQuad({
            width,
            height,
            colorTl: premultipliedColorTl,
            colorTr: premultipliedColorTr,
            colorBl: premultipliedColorBl,
            colorBr: premultipliedColorBr,
            texture,
            textureOptions,
            zIndex,
            shader: shader.shader,
            shaderProps: shader.getResolvedProps(),
            alpha: worldAlpha,
            clippingRect,
            tx: gt.tx,
            ty: gt.ty,
            ta: gt.ta,
            tb: gt.tb,
            tc: gt.tc,
            td: gt.td,
            renderCoords,
            rtt,
            parentHasRenderTexture: this.parentHasRenderTexture,
            framebufferDimensions: this.framebufferDimensions,
        });
    }
    //#region Properties
    get id() {
        return this._id;
    }
    get x() {
        return this.props.x;
    }
    set x(value) {
        if (this.props.x !== value) {
            this.props.x = value;
            this.setUpdateType(UpdateType.Local);
        }
    }
    get absX() {
        return (this.props.x +
            (this.props.parent?.absX || this.props.parent?.globalTransform?.tx || 0));
    }
    get absY() {
        return this.props.y + (this.props.parent?.absY ?? 0);
    }
    get y() {
        return this.props.y;
    }
    set y(value) {
        if (this.props.y !== value) {
            this.props.y = value;
            this.setUpdateType(UpdateType.Local);
        }
    }
    get width() {
        return this.props.width;
    }
    set width(value) {
        if (this.props.width !== value) {
            this.props.width = value;
            this.setUpdateType(UpdateType.Local);
            if (this.props.rtt) {
                this.texture = this.stage.txManager.loadTexture('RenderTexture', {
                    width: this.width,
                    height: this.height,
                });
                this.textureOptions.preload = true;
                this.setUpdateType(UpdateType.RenderTexture);
            }
        }
    }
    get height() {
        return this.props.height;
    }
    set height(value) {
        if (this.props.height !== value) {
            this.props.height = value;
            this.setUpdateType(UpdateType.Local);
            if (this.props.rtt) {
                this.texture = this.stage.txManager.loadTexture('RenderTexture', {
                    width: this.width,
                    height: this.height,
                });
                this.textureOptions.preload = true;
                this.setUpdateType(UpdateType.RenderTexture);
            }
        }
    }
    get scale() {
        // The CoreNode `scale` property is only used by Animations.
        // Unlike INode, `null` should never be possibility for Animations.
        return this.scaleX;
    }
    set scale(value) {
        // The CoreNode `scale` property is only used by Animations.
        // Unlike INode, `null` should never be possibility for Animations.
        this.scaleX = value;
        this.scaleY = value;
    }
    get scaleX() {
        return this.props.scaleX;
    }
    set scaleX(value) {
        if (this.props.scaleX !== value) {
            this.props.scaleX = value;
            this.setUpdateType(UpdateType.ScaleRotate);
        }
    }
    get scaleY() {
        return this.props.scaleY;
    }
    set scaleY(value) {
        if (this.props.scaleY !== value) {
            this.props.scaleY = value;
            this.setUpdateType(UpdateType.ScaleRotate);
        }
    }
    get mount() {
        return this.props.mount;
    }
    set mount(value) {
        if (this.props.mountX !== value || this.props.mountY !== value) {
            this.props.mountX = value;
            this.props.mountY = value;
            this.props.mount = value;
            this.setUpdateType(UpdateType.Local);
        }
    }
    get mountX() {
        return this.props.mountX;
    }
    set mountX(value) {
        if (this.props.mountX !== value) {
            this.props.mountX = value;
            this.setUpdateType(UpdateType.Local);
        }
    }
    get mountY() {
        return this.props.mountY;
    }
    set mountY(value) {
        if (this.props.mountY !== value) {
            this.props.mountY = value;
            this.setUpdateType(UpdateType.Local);
        }
    }
    get pivot() {
        return this.props.pivot;
    }
    set pivot(value) {
        if (this.props.pivotX !== value || this.props.pivotY !== value) {
            this.props.pivotX = value;
            this.props.pivotY = value;
            this.props.pivot = value;
            this.setUpdateType(UpdateType.Local);
        }
    }
    get pivotX() {
        return this.props.pivotX;
    }
    set pivotX(value) {
        if (this.props.pivotX !== value) {
            this.props.pivotX = value;
            this.setUpdateType(UpdateType.Local);
        }
    }
    get pivotY() {
        return this.props.pivotY;
    }
    set pivotY(value) {
        if (this.props.pivotY !== value) {
            this.props.pivotY = value;
            this.setUpdateType(UpdateType.Local);
        }
    }
    get rotation() {
        return this.props.rotation;
    }
    set rotation(value) {
        if (this.props.rotation !== value) {
            this.props.rotation = value;
            this.setUpdateType(UpdateType.ScaleRotate);
        }
    }
    get alpha() {
        return this.props.alpha;
    }
    set alpha(value) {
        this.props.alpha = value;
        this.setUpdateType(UpdateType.PremultipliedColors | UpdateType.WorldAlpha);
    }
    get autosize() {
        return this.props.autosize;
    }
    set autosize(value) {
        this.props.autosize = value;
    }
    get clipping() {
        return this.props.clipping;
    }
    set clipping(value) {
        this.props.clipping = value;
        this.setUpdateType(UpdateType.Clipping);
    }
    get color() {
        return this.props.color;
    }
    set color(value) {
        this.colorTop = value;
        this.colorBottom = value;
        this.colorLeft = value;
        this.colorRight = value;
        this.props.color = value;
        this.setUpdateType(UpdateType.PremultipliedColors);
    }
    get colorTop() {
        return this.props.colorTop;
    }
    set colorTop(value) {
        if (this.props.colorTl !== value || this.props.colorTr !== value) {
            this.colorTl = value;
            this.colorTr = value;
        }
        this.props.colorTop = value;
        this.setUpdateType(UpdateType.PremultipliedColors);
    }
    get colorBottom() {
        return this.props.colorBottom;
    }
    set colorBottom(value) {
        if (this.props.colorBl !== value || this.props.colorBr !== value) {
            this.colorBl = value;
            this.colorBr = value;
        }
        this.props.colorBottom = value;
        this.setUpdateType(UpdateType.PremultipliedColors);
    }
    get colorLeft() {
        return this.props.colorLeft;
    }
    set colorLeft(value) {
        if (this.props.colorTl !== value || this.props.colorBl !== value) {
            this.colorTl = value;
            this.colorBl = value;
        }
        this.props.colorLeft = value;
        this.setUpdateType(UpdateType.PremultipliedColors);
    }
    get colorRight() {
        return this.props.colorRight;
    }
    set colorRight(value) {
        if (this.props.colorTr !== value || this.props.colorBr !== value) {
            this.colorTr = value;
            this.colorBr = value;
        }
        this.props.colorRight = value;
        this.setUpdateType(UpdateType.PremultipliedColors);
    }
    get colorTl() {
        return this.props.colorTl;
    }
    set colorTl(value) {
        this.props.colorTl = value;
        this.setUpdateType(UpdateType.PremultipliedColors);
    }
    get colorTr() {
        return this.props.colorTr;
    }
    set colorTr(value) {
        this.props.colorTr = value;
        this.setUpdateType(UpdateType.PremultipliedColors);
    }
    get colorBl() {
        return this.props.colorBl;
    }
    set colorBl(value) {
        this.props.colorBl = value;
        this.setUpdateType(UpdateType.PremultipliedColors);
    }
    get colorBr() {
        return this.props.colorBr;
    }
    set colorBr(value) {
        this.props.colorBr = value;
        this.setUpdateType(UpdateType.PremultipliedColors);
    }
    // we're only interested in parent zIndex to test
    // if we should use node zIndex is higher then parent zIndex
    get zIndexLocked() {
        return this.props.zIndexLocked || 0;
    }
    set zIndexLocked(value) {
        this.props.zIndexLocked = value;
        this.setUpdateType(UpdateType.CalculatedZIndex | UpdateType.Children);
        this.children.forEach((child) => {
            child.setUpdateType(UpdateType.CalculatedZIndex);
        });
    }
    get zIndex() {
        return this.props.zIndex;
    }
    set zIndex(value) {
        this.props.zIndex = value;
        this.setUpdateType(UpdateType.CalculatedZIndex | UpdateType.Children);
        this.children.forEach((child) => {
            child.setUpdateType(UpdateType.CalculatedZIndex);
        });
    }
    get parent() {
        return this.props.parent;
    }
    set parent(newParent) {
        const oldParent = this.props.parent;
        if (oldParent === newParent) {
            return;
        }
        this.props.parent = newParent;
        if (oldParent) {
            const index = oldParent.children.indexOf(this);
            assertTruthy(index !== -1, "CoreNode.parent: Node not found in old parent's children!");
            oldParent.children.splice(index, 1);
            oldParent.setUpdateType(UpdateType.Children | UpdateType.ZIndexSortedChildren);
        }
        if (newParent) {
            newParent.children.push(this);
            // Since this node has a new parent, to be safe, have it do a full update.
            this.setUpdateType(UpdateType.All);
            // Tell parent that it's children need to be updated and sorted.
            newParent.setUpdateType(UpdateType.Children | UpdateType.ZIndexSortedChildren);
            if (newParent.rtt || newParent.parentHasRenderTexture) {
                this.setRTTUpdates(UpdateType.All);
            }
        }
        this.updateScaleRotateTransform();
    }
    get rtt() {
        return this.props.rtt;
    }
    set rtt(value) {
        if (this.props.rtt === true) {
            this.props.rtt = value;
            // unload texture if we used to have a render texture
            if (value === false && this.texture !== null) {
                this.unloadTexture();
                this.setUpdateType(UpdateType.All);
                this.children.forEach((child) => {
                    child.parentHasRenderTexture = false;
                });
                this.stage.renderer?.removeRTTNode(this);
                return;
            }
        }
        // if the new value is false and we didnt have rtt previously, we don't need to do anything
        if (value === false) {
            return;
        }
        // load texture
        this.texture = this.stage.txManager.loadTexture('RenderTexture', {
            width: this.width,
            height: this.height,
        });
        this.textureOptions.preload = true;
        this.props.rtt = true;
        this.hasRTTupdates = true;
        this.setUpdateType(UpdateType.All);
        this.children.forEach((child) => {
            child.setUpdateType(UpdateType.All);
        });
        // Store RTT nodes in a separate list
        this.stage.renderer?.renderToTexture(this);
    }
    get shader() {
        return this.props.shader;
    }
    set shader(value) {
        if (this.props.shader === value) {
            return;
        }
        this.props.shader = value;
        this.setUpdateType(UpdateType.IsRenderable);
    }
    get src() {
        return this.props.src;
    }
    set src(imageUrl) {
        if (this.props.src === imageUrl) {
            return;
        }
        this.props.src = imageUrl;
        if (!imageUrl) {
            this.texture = null;
            return;
        }
        this.texture = this.stage.txManager.loadTexture('ImageTexture', {
            src: imageUrl,
        });
    }
    /**
     * Returns the framebuffer dimensions of the node.
     * If the node has a render texture, the dimensions are the same as the node's dimensions.
     * If the node does not have a render texture, the dimensions are inherited from the parent.
     * If the node parent has a render texture and the node is a render texture, the nodes dimensions are used.
     */
    get framebufferDimensions() {
        if (this.parentHasRenderTexture && !this.rtt && this.parent) {
            return this.parent.framebufferDimensions;
        }
        return { width: this.width, height: this.height };
    }
    /**
     * Returns the parent render texture node if it exists.
     */
    get parentRenderTexture() {
        let parent = this.parent;
        while (parent) {
            if (parent.rtt) {
                return parent;
            }
            parent = parent.parent;
        }
        return null;
    }
    get texture() {
        return this.props.texture;
    }
    set texture(value) {
        if (this.props.texture === value) {
            return;
        }
        const oldTexture = this.props.texture;
        if (oldTexture) {
            oldTexture.setRenderableOwner(this, false);
            this.unloadTexture();
        }
        this.props.texture = value;
        if (value) {
            value.setRenderableOwner(this, this.isRenderable);
            this.loadTexture();
        }
        this.setUpdateType(UpdateType.IsRenderable);
    }
    set textureOptions(value) {
        this.props.textureOptions = value;
    }
    get textureOptions() {
        return this.props.textureOptions;
    }
    setRTTUpdates(type) {
        this.hasRTTupdates = true;
        this.parent?.setRTTUpdates(type);
    }
    animate(props, settings) {
        const animation = new CoreAnimation(this, props, settings);
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
        const controller = new CoreAnimationController(this.stage.animationManager, animation);
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return controller;
    }
    flush() {
        // no-op
    }
}
//# sourceMappingURL=CoreNode.js.map